import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toastError, toastSuccess } from '../utils/toastHelper'
import { useTheme } from '../hooks/useTheme'
import apiClient from '../services/apiClient'
import SimpleHeader from '../components/common/SimpleHeader'
import Footer from '../components/common/Footer'
import LoadingSpinner from '../components/common/LoadingSpinner'

// Tipos genéricos para soportar múltiples proveedores
interface PaymentStatusResponse {
  status: 'success' | 'error' | 'pending'
  message: string
  reservaId?: string
  reservaEmail?: string
  paymentDetails?: Record<string, any>
}

interface PaymentHandler {
  getPaymentId: () => string | null
  checkStatus: (paymentId: string) => Promise<PaymentStatusResponse>
  cleanup: () => void
}

type PaymentMethodType = 'getnet' | 'khipu' | 'transbank'

const PaymentReturnPage: React.FC = () => {
  const navigate = useNavigate()
  const { colors } = useTheme()
  const VITE_API_URL = import.meta.env.VITE_API_URL

  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'pending'>('loading')
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusResponse | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType | null>(null)

  // ===== HANDLERS ESPECÍFICOS POR PROVEEDOR =====

  // Handler para Getnet
  const getnetHandler: PaymentHandler = {
    getPaymentId: () => localStorage.getItem('payment_request_id'),
    checkStatus: async (paymentId: string) => {
      // ✅ Usar apiClient en lugar de fetch
      const response = await apiClient.get(`/api/payments/getnet/status/${paymentId}`)
      const data = response.data

      // Mapear respuesta Getnet a formato genérico
      return {
        status:
          data.status.status === 'APPROVED'
            ? 'success'
            : data.status.status === 'REJECTED'
              ? 'error'
              : 'pending',
        message: data.status.message,
        reservaId: data.reserva?.id,
        reservaEmail: data.reserva?.huesped?.email,
        paymentDetails: {
          authorization: data.payment?.[0]?.authorization,
          receipt: data.payment?.[0]?.receipt,
          methodName: data.payment?.[0]?.paymentMethodName,
        },
      }
    },
    cleanup: () => localStorage.removeItem('payment_request_id'),
  }

  // Handler para Khipu
  const khipuHandler: PaymentHandler = {
    getPaymentId: () => localStorage.getItem('khipu_payment_id'),
    checkStatus: async (paymentId: string) => {
      // ✅ Usar apiClient en lugar de fetch
      const response = await apiClient.get(`/api/payments/khipu/status/${paymentId}`)
      const data = response.data

      // Mapear respuesta Khipu a formato genérico
      return {
        status:
          data.status === 'done'
            ? 'success'
            : data.status === 'cancelled' || data.status === 'expired' || data.status === 'pending'
              ? data.status === 'pending'
                ? 'pending'
                : 'error'
              : 'pending',
        message:
          data.status === 'done'
            ? 'Pago completado correctamente'
            : data.status === 'pending'
              ? 'Estamos procesando tu pago'
              : 'El pago fue cancelado o expiró',
        reservaId: localStorage.getItem('payment_reserva_id') || undefined,
        reservaEmail: data.payer_email,
        paymentDetails: {
          paymentId: data.payment_id,
          amount: data.amount,
          methodName: 'Transferencia Bancaria (Khipu)',
        },
      }
    },
    cleanup: () => {
      localStorage.removeItem('khipu_payment_id')
      localStorage.removeItem('payment_reserva_id')
    },
  }

  // Mapeo de response_code de Transbank a mensaje descriptivo
  const getTransbankErrorMessage = (responseCode: number | string | undefined): string => {
    const code = Number(responseCode)
    const messages: Record<number, string> = {
      0: 'Transacción aprobada',
      '-1': 'Tu banco rechazó la transacción',
      '-2': 'Por favor intenta nuevamente',
      '-3': 'Error en la transacción',
      '-4': 'Tu banco rechazó la transacción',
      '-5': 'Error en la tasa de cambio',
      '-6': 'Superaste el cupo máximo mensual',
      '-7': 'Superaste el límite diario por transacción',
      '-8': 'Este rubro no está autorizado para tu tarjeta',
    }
    return messages[code] || `Error en transacción (código: ${responseCode})`
  }

  // Handler para Transbank
  const transbankHandler: PaymentHandler = {
    getPaymentId: () => {
      // Transbank devuelve token_ws cuando el pago fue completado
      // O TBK_ORDEN_COMPRA cuando fue cancelado/no completado
      const urlParams = new URLSearchParams(window.location.search)
      const token = urlParams.get('token_ws')
      const tbkOrdenCompra = urlParams.get('TBK_ORDEN_COMPRA')

      // Si viene TBK_ORDEN_COMPRA, retornar un identificador especial para cancelación
      if (tbkOrdenCompra) {
        return `CANCELLED:${tbkOrdenCompra}`
      }

      return token || localStorage.getItem('transbank_token')
    },
    checkStatus: async (paymentId: string) => {
      // Detectar si es una cancelación
      if (paymentId.startsWith('CANCELLED:')) {
        const ordenCompra = paymentId.replace('CANCELLED:', '')
        return {
          status: 'error',
          message: 'El pago fue cancelado. Si deseas reintentar, vuelve al carrito.',
          paymentDetails: {
            buyOrder: ordenCompra,
            methodName: 'Webpay Plus (Transbank)',
          },
        }
      }

      // Si no es cancelación, proceder con el commit normal
      const response = await apiClient.post('/api/payments/transbank/commit', `token_ws=${paymentId}`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      })
      const data = response.data

      // Mapear respuesta Transbank a formato genérico
      return {
        status:
          data.approved === true
            ? 'success'
            : data.status === 'AUTHORIZED'
              ? 'success'
              : 'error',
        message:
          data.approved === true
            ? 'Pago aprobado por Transbank. Revisa tu email para la confirmación.'
            : data.status === 'AUTHORIZED'
              ? 'Transacción autorizada'
              : `Pago rechazado: ${getTransbankErrorMessage(data.response_code)}`,
        reservaId: localStorage.getItem('payment_reserva_id') || undefined,
        paymentDetails: {
          buyOrder: data.buy_order,
          authorizationCode: data.authorization_code,
          cardNumber: data.card_number,
          amount: data.amount,
          methodName: 'Webpay Plus (Transbank)',
          responseCode: data.response_code,
        },
      }
    },
    cleanup: () => {
      localStorage.removeItem('transbank_token')
      localStorage.removeItem('payment_reserva_id')
    },
  }

  // Mapeo de métodos de pago a handlers
  const getHandler = (method: PaymentMethodType): PaymentHandler => {
    switch (method) {
      case 'khipu':
        return khipuHandler
      case 'transbank':
        return transbankHandler
      case 'getnet':
      default:
        return getnetHandler
    }
  }

  useEffect(() => {
    const checkPayment = async (retryCount = 0, maxRetries = 3) => {
      // Detectar método de pago
      const method = (localStorage.getItem('payment_method') || 'getnet') as PaymentMethodType
      setPaymentMethod(method)

      const handler = getHandler(method)
      const paymentId = handler.getPaymentId()

      if (!paymentId) {
        setStatus('error')
        setLoading(false)
        toastError('No se encontró información del pago')
        return
      }

      try {
        const result = await handler.checkStatus(paymentId)
        setPaymentStatus(result)

        if (result.status === 'success') {
          setStatus('success')
          toastSuccess('¡Pago aprobado! Tu reserva ha sido confirmada')
          // NO limpiar localStorage aún - ReservationConfirmationPage lo necesita
          // handler.cleanup() se llamará al salir de esta página
        } else if (result.status === 'error') {
          setStatus('error')
          toastError('Pago rechazado o cancelado. Por favor intenta nuevamente')
        } else if (result.status === 'pending') {
          setStatus('pending')
          // Auto-refresh cada 5 segundos si está pendiente
          setTimeout(() => checkPayment(0, maxRetries), 5000)
        }
      } catch (error: any) {
        console.error('Error verificando estado de pago:', error)

        // Manejo específico de timeout
        if (error.code === 'ECONNABORTED' && retryCount < maxRetries) {
          // Timeout - reintentar con backoff
          const delay = Math.min(2000 * Math.pow(2, retryCount), 10000) // Max 10s
          console.log(`Reintentando (${retryCount + 1}/${maxRetries}) en ${delay}ms...`)
          setTimeout(() => checkPayment(retryCount + 1, maxRetries), delay)
          return
        }

        // Error final o no es timeout
        setStatus('error')
        toastError('Error al verificar el estado del pago. Por favor, intenta más tarde.')
      } finally {
        setLoading(false)
      }
    }

    checkPayment()
  }, [])

  // Estado: Cargando
  if (loading || status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <SimpleHeader
          title="Estado del Pago"
          subtitle="Verifica el resultado de tu transacción"
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-6 text-lg text-gray-600 font-medium">
              Verificando tu pago...
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Por favor espera un momento
            </p>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  // Estado: Pago Exitoso
  if (status === 'success' && paymentStatus) {
    const details = paymentStatus.paymentDetails || {}

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <SimpleHeader
          title="Estado del Pago"
          subtitle="Verifica el resultado de tu transacción"
        />
        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-green-100">
            {/* Icono de éxito */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
              <svg
                className="h-10 w-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            {/* Título */}
            <h2 className="text-center text-3xl font-bold text-gray-900 mb-2">
              ¡Pago Exitoso!
            </h2>
            <p className="text-center text-gray-600 mb-6">
              {paymentStatus.message}
            </p>

            {/* Detalles del pago */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-3">
              {details.authorization && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Código de autorización:</span>
                  <span className="text-sm font-mono font-semibold text-gray-900">
                    {details.authorization}
                  </span>
                </div>
              )}

              {details.receipt && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Comprobante:</span>
                  <span className="text-sm font-mono font-semibold text-gray-900">
                    {details.receipt}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Método de pago:</span>
                <span className="text-sm font-semibold text-gray-900">
                  {details.methodName || 'Transferencia Bancaria'}
                </span>
              </div>

              {paymentStatus.reservaId && (
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-sm text-gray-600">ID de reserva:</span>
                  <span className="text-sm font-mono font-semibold text-gray-900">
                    {paymentStatus.reservaId}
                  </span>
                </div>
              )}
            </div>

            {/* Botones de acción */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  if (paymentStatus.reservaId) {
                    // Limpiar localStorage después de navegar
                    localStorage.removeItem('payment_method')
                    localStorage.removeItem('khipu_payment_id')
                    localStorage.removeItem('payment_request_id')
                    localStorage.removeItem('payment_reserva_id')
                    localStorage.removeItem('payment_reserva_email')
                    // El backend ahora retorna TODOS los espacios cuando se busca por reserva_id
                    navigate(`/confirmacion-reserva?reserva_id=${encodeURIComponent(paymentStatus.reservaId)}`)
                  } else {
                    navigate('/')
                  }
                }}
                className="w-full btn-lg font-semibold text-white rounded-lg py-3 transition-all"
                style={{
                  backgroundColor: colors.primary,
                  opacity: 1
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                👀 Ver mi reserva
              </button>

              <button
                onClick={() => navigate('/')}
                className="w-full btn-lg font-semibold rounded-lg py-3 border-2 transition-all"
                style={{
                  color: colors.primary,
                  borderColor: colors.primary,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${colors.primaryLight}20`)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                🏠 Volver al inicio
              </button>
            </div>

              {/* Información adicional */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  Recibirás un correo electrónico con los detalles de tu reserva
                </p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  // Estado: Pago Pendiente
  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <SimpleHeader
          title="Estado del Pago"
          subtitle="Verifica el resultado de tu transacción"
        />
        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <div className="bg-white shadow-xl rounded-2xl p-8 border border-yellow-100">
            {/* Icono de pendiente */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-6">
              <svg
                className="h-10 w-10 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            {/* Título */}
            <h2 className="text-center text-3xl font-bold text-gray-900 mb-2">
              Pago Pendiente
            </h2>
            <p className="text-center text-gray-600 mb-6">
              {paymentStatus?.message || 'Estamos procesando tu pago'}
            </p>

            {/* Spinner */}
            <div className="flex justify-center mb-6">
              <LoadingSpinner size="md" />
            </div>

            <p className="text-center text-sm text-gray-500 mb-6">
              Te enviaremos un correo cuando se confirme tu pago
            </p>

              {/* Botón */}
              <button
                onClick={() => navigate('/')}
                className="w-full btn-lg font-semibold rounded-lg py-3 border-2 transition-all"
                style={{
                  color: colors.primary,
                  borderColor: colors.primary,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${colors.primaryLight}20`)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                🏠 Volver al inicio
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  // Estado: Error
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SimpleHeader
        title="Estado del Pago"
        subtitle="Verifica el resultado de tu transacción"
      />

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white shadow-xl rounded-2xl p-8 border border-red-100">
          {/* Icono de error */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
            <svg
              className="h-10 w-10 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>

          {/* Título */}
          <h2 className="text-center text-3xl font-bold text-gray-900 mb-2">
            Pago No Completado
          </h2>
          <p className="text-center text-gray-600 mb-6">
            {paymentStatus?.message || 'Hubo un problema al procesar tu pago'}
          </p>

          {/* Botones de acción */}
          <div className="space-y-3">
            <button
              onClick={() => {
                // Limpiar localStorage y permitir reintentar
                const method = localStorage.getItem('payment_method') || 'getnet'
                if (method === 'khipu') {
                  localStorage.removeItem('khipu_payment_id')
                  localStorage.removeItem('payment_reserva_id')
                } else if (method === 'transbank') {
                  localStorage.removeItem('transbank_token')
                  localStorage.removeItem('payment_reserva_id')
                } else {
                  localStorage.removeItem('payment_request_id')
                }
                navigate('/')
              }}
              className="w-full btn-lg font-semibold text-white rounded-lg py-3 transition-all"
              style={{
                backgroundColor: colors.primary,
                opacity: 1
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              🔄 Intentar nuevamente
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full btn-lg font-semibold rounded-lg py-3 border-2 transition-all"
              style={{
                color: colors.primary,
                borderColor: colors.primary,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${colors.primaryLight}20`)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              🏠 Volver al inicio
            </button>
          </div>

          {/* Ayuda */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Si el problema persiste, por favor contáctanos
            </p>
          </div>
        </div>
      </div>
      </div>

      <Footer />
    </div>
  )
}

export default PaymentReturnPage
